import { motion } from "framer-motion";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { Kicker, SectionHeader } from "@/components/Bits";
import { CtaRow, CheckAvailBtn } from "@/components/CTAButtons";
import { ParallaxImage } from "@/components/ParallaxImage";
import { EditorialMarquee } from "@/components/EditorialMarquee";
import { FaqList } from "@/components/FaqList";
import { scaleIn, maskChild, staggerParent } from "@/lib/motion";
import { Check, ArrowRight } from "lucide-react";
import * as Icons from "lucide-react";

export const LandingTemplate = ({
  seo,
  kicker,
  heroTitle,
  heroLines = [],
  heroImage,
  intro,
  audiences = [],
  features = [],
  bandImage,
  bandTitle,
  bandText,
  benefits = [],
  keywords = [],
  faqs = [],
}) => (
  <>
    <Seo {...seo} image={heroImage} />

    {/* HERO */}
    <section className="relative flex min-h-[90vh] items-end overflow-hidden bg-charcoal">
      <motion.div variants={scaleIn} initial="hidden" animate="show" className="absolute inset-0">
        <img src={heroImage} alt={heroTitle} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/25" />
      </motion.div>
      <div className="relative mx-auto w-full max-w-[1400px] px-5 pb-16 pt-32 md:px-10 md:pb-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <Kicker tone="yellow">{kicker}</Kicker>
        </motion.div>
        <motion.h1 variants={staggerParent} initial="hidden" animate="show" className="mt-5 font-display text-6xl uppercase leading-[0.85] tracking-tight text-white sm:text-7xl lg:text-8xl">
          {heroLines.map((line, i) => (
            <span key={i} className="mask-reveal">
              <motion.span variants={maskChild} custom={i} className="block">
                {line}
              </motion.span>
            </span>
          ))}
        </motion.h1>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7, duration: 0.6 }} className="mt-8">
          <CtaRow onDark />
        </motion.div>
      </div>
    </section>

    {/* INTRO */}
    <section className="mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-32">
      <div className="grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <SectionHeader kicker="The Overview" title={bandTitle || heroTitle} sub={intro} />
        </div>
        <div className="lg:col-span-5">
          {audiences.length > 0 && (
            <Reveal className="border-2 border-charcoal bg-paper p-8">
              <p className="font-heading text-sm font-black uppercase tracking-wide text-brand-red">Who this is for</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {audiences.map((a) => (
                  <span key={a} className="border border-charcoal/20 px-3 py-1.5 font-body text-sm text-charcoal/80">
                    {a}
                  </span>
                ))}
              </div>
            </Reveal>
          )}
        </div>
      </div>
    </section>

    {/* FEATURES GRID */}
    {features.length > 0 && (
      <section className="border-y border-charcoal/10 bg-bone">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 border-l border-charcoal/10 md:grid-cols-2 lg:grid-cols-3">
          {features.map((f, i) => {
            const Icon = Icons[f.icon] || Icons.Sparkles;
            return (
              <Reveal
                key={f.title}
                i={i % 3}
                className="group border-b border-r border-charcoal/10 p-8 transition-colors duration-300 hover:bg-brand-yellow md:p-10"
              >
                <Icon className="h-8 w-8 text-brand-red" />
                <h3 className="mt-6 font-heading text-xl font-black text-charcoal">{f.title}</h3>
                <p className="mt-3 font-body text-sm leading-relaxed text-fog group-hover:text-charcoal/80">{f.desc}</p>
              </Reveal>
            );
          })}
        </div>
      </section>
    )}

    {/* IMAGE BAND + BENEFITS */}
    <section className="mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-32">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <ParallaxImage src={bandImage || heroImage} alt={bandTitle || heroTitle} height="h-[70vh]" />
        </Reveal>
        <div>
          <SectionHeader kicker="Why Guests Choose Us" title={bandText ? "Everything you need, nothing you don't" : heroTitle} />
          {bandText && <Reveal i={2}><p className="mt-5 font-body text-base leading-relaxed text-fog">{bandText}</p></Reveal>}
          <ul className="mt-8 space-y-4">
            {benefits.map((b, i) => (
              <Reveal as="li" i={i} key={b} className="flex items-start gap-3">
                <Check className="mt-0.5 h-5 w-5 shrink-0 text-brand-red" />
                <span className="font-body text-charcoal/90">{b}</span>
              </Reveal>
            ))}
          </ul>
          <Reveal i={2} className="mt-10"><CheckAvailBtn /></Reveal>
        </div>
      </div>
    </section>

    <EditorialMarquee items={["Book Direct", "Best Rate", "Super 8 Ottawa", "Right off I-35"]} />

    {/* FAQ */}
    {faqs.length > 0 && (
      <section className="mx-auto max-w-4xl px-5 py-24 md:px-10 md:py-32">
        <SectionHeader kicker="Good To Know" title="Frequently asked" />
        <div className="mt-12">
          <FaqList faqs={faqs} testidPrefix="landing-faq" />
        </div>
      </section>
    )}

    {/* KEYWORD FOOTER (semantic SEO) */}
    {keywords.length > 0 && (
      <section className="border-t border-charcoal/10 bg-bone py-16">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <p className="font-body text-xs font-semibold uppercase tracking-[0.3em] text-fog">Popular searches</p>
          <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2">
            {keywords.map((k) => (
              <span key={k} className="flex items-center gap-2 font-body text-sm text-charcoal/70">
                <ArrowRight className="h-3 w-3 text-brand-red" /> {k}
              </span>
            ))}
          </div>
        </div>
      </section>
    )}
  </>
);
